import { Box, Divider, FormField, InfoIcon, SegmentedToggle } from '@wix/design-system';
import React, { useContext } from 'react';

import { RicosContext, ZIndexContext } from './../../../ricos-context';
import {
  PollData_Layout_PollLayout_Direction,
  PollData_Layout_PollLayout_Type,
} from './../../../ricos-schema/next-schema';
import type { LogBiFn } from './../../../_shared/bi-context-provider/bi-context-provider';
import { logInvariant } from './../../../_shared/log-invariant';
import { useNodeAttrs } from './../../../_shared/use-node-attrs';
import { LTRIcon, RTLIcon } from './../../../plugin-commons';
import {
  ThumbnailPicker,
  ToggleButton,
  usePanelContainerElement,
} from './../../../ui-components';

import { LayoutGridIcon } from '../../assets/icons/LayoutGridIcon';
import { LayoutListIcon } from '../../assets/icons/LayoutListIcon';
import {
  LAYOUT_SETTINGS_DIRECTION_TOGGLE,
  LAYOUT_SETTINGS_IMAGE_TO_ANSWERS_TOGGLE,
  LAYOUT_SETTINGS_IMAGE_TO_QUESTION_TOGGLE,
  LAYOUT_SETTINGS_LAYOUT_THUMBNAIL,
  LAYOUT_SETTINGS_LAYOUT_THUMBNAIL_PICKER,
} from '../../data-hooks';
import { Section } from './section';

type LayoutSettingsSectionProps = {
  nodeId: string;
  logBi: LogBiFn;
};

export const LayoutSettingsSection: React.FC<LayoutSettingsSectionProps> = ({ nodeId, logBi }) => {
  const { t } = useContext(RicosContext);
  const zIndexService = useContext(ZIndexContext);
  const { pmNodeAttrs, patchPmNodeAttrs } = useNodeAttrs(nodeId, 'POLL');
  const containerEl = usePanelContainerElement();

  if (!pmNodeAttrs) {
    logInvariant(Error('Poll settings: node attrs not found'));
    return null;
  }

  const { poll, options } = pmNodeAttrs.layout ?? {};

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Layout_Section_Question_Header')}>
        <ToggleButton
          checked={Boolean(poll?.enableImage)}
          title={t('Poll_PollSettings_Tab_Layout_Section_Question_Image')}
          dataHook={LAYOUT_SETTINGS_IMAGE_TO_QUESTION_TOGGLE}
          onChange={() => {
            logBi({
              dataHook: LAYOUT_SETTINGS_IMAGE_TO_QUESTION_TOGGLE,
              value: !poll?.enableImage,
            });
            patchPmNodeAttrs({
              layout: { ...pmNodeAttrs.layout, poll: { ...poll, enableImage: !poll?.enableImage } },
            });
          }}
          size="small"
        />
      </Section>
      <Box marginInline="-18px">
        <Divider />
      </Box>
      <Section title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Header')}>
        <ThumbnailPicker
          dataHook={LAYOUT_SETTINGS_LAYOUT_THUMBNAIL_PICKER}
          logBi={logBi}
          dataSource={[
            {
              dataHook: LAYOUT_SETTINGS_LAYOUT_THUMBNAIL + PollData_Layout_PollLayout_Type.LIST,
              value: PollData_Layout_PollLayout_Type.LIST,
              label: t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout_List'),
              renderBackgroundImage: props => <LayoutListIcon {...props} />,
            },
            {
              dataHook: LAYOUT_SETTINGS_LAYOUT_THUMBNAIL + PollData_Layout_PollLayout_Type.GRID,
              value: PollData_Layout_PollLayout_Type.GRID,
              label: t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout_Grid'),
              renderBackgroundImage: props => <LayoutGridIcon {...props} />,
            },
          ]}
          selectedValue={poll?.type ?? PollData_Layout_PollLayout_Type.LIST}
          title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Layout')}
          onChange={type => {
            patchPmNodeAttrs({
              layout: {
                ...pmNodeAttrs.layout,
                poll: { ...poll, type: type as PollData_Layout_PollLayout_Type },
              },
            });

            window.dispatchEvent(new Event('resize'));
          }}
        />
        <ToggleButton
          checked={Boolean(options?.enableImage)}
          title={t('Poll_PollSettings_Tab_Layout_Section_Answers_Image')}
          dataHook={LAYOUT_SETTINGS_IMAGE_TO_ANSWERS_TOGGLE}
          onChange={() => {
            logBi({
              dataHook: LAYOUT_SETTINGS_IMAGE_TO_ANSWERS_TOGGLE,
              value: !options?.enableImage,
            });
            patchPmNodeAttrs({
              layout: {
                ...pmNodeAttrs.layout,
                options: { ...options, enableImage: !options?.enableImage },
              },
            });
          }}
          size="small"
        />
      </Section>
      <Box marginInline="-18px">
        <Divider />
      </Box>
      <Section
        title={
          <>
            {t('Poll_PollSettings_Tab_Layout_Section_TextDirection_Header')}
            &nbsp;
            <InfoIcon
              content={t('Poll_PollSettings_Tab_Layout_Section_TextDirection_Header_Tooltip')}
              size="small"
              tooltipProps={{
                appendTo: containerEl,
                zIndex: zIndexService.getZIndex('TOOLTIP'),
              }}
            />
          </>
        }
      >
        <FormField
          label={t('CollapsibleList_CollapsibleListSettings_Tab_Settings_Direction_Title')}
        >
          <Box marginTop="SP1" />
          <SegmentedToggle
            size="small"
            selected={pmNodeAttrs.layout?.poll?.direction}
            defaultSelected={pmNodeAttrs.layout?.poll?.direction}
            onClick={(_, direction) => {
              logBi({
                dataHook: LAYOUT_SETTINGS_DIRECTION_TOGGLE,
                value: direction,
              });
              patchPmNodeAttrs({
                layout: {
                  ...pmNodeAttrs.layout,
                  poll: { ...poll, direction: direction as PollData_Layout_PollLayout_Direction },
                },
              });
            }}
            dataHook={LAYOUT_SETTINGS_DIRECTION_TOGGLE}
          >
            <SegmentedToggle.Button
              key={PollData_Layout_PollLayout_Direction.LTR}
              value={PollData_Layout_PollLayout_Direction.LTR}
              size="small"
              prefixIcon={<LTRIcon />}
            >
              {t('Poll_PollSettings_Tab_Layout_Section_TextDirection_LTR')}
            </SegmentedToggle.Button>
            <SegmentedToggle.Button
              key={PollData_Layout_PollLayout_Direction.RTL}
              value={PollData_Layout_PollLayout_Direction.RTL}
              size="small"
              prefixIcon={<RTLIcon />}
            >
              {t('Poll_PollSettings_Tab_Layout_Section_TextDirection_RTL')}
            </SegmentedToggle.Button>
          </SegmentedToggle>
        </FormField>
      </Section>
    </>
  );
};
